import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { resumeAPI, interviewAPI } from '../services/api';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const MODES = [
  { id: 'text_chat', icon: '💬', title: 'Text Chat', desc: 'Type your answers in a chat interface. Great for practice.' },
  { id: 'mcq', icon: '📝', title: 'MCQ Mode', desc: 'Answer multiple choice questions. Quick knowledge check.' },
  { id: 'voice', icon: '🎙️', title: 'Voice Mode', desc: 'Record your answers. Get feedback on speaking skills.' },
];

const TYPES = [
  { id: 'behavioral', label: 'Behavioral' },
  { id: 'technical', label: 'Technical' },
  { id: 'hr', label: 'HR' },
  { id: 'mixed', label: 'Mixed (All)' },
];

const DIFFICULTIES = [
  { id: 'easy', label: 'Easy', desc: 'Beginner level' },
  { id: 'medium', label: 'Medium', desc: 'Intermediate' },
  { id: 'hard', label: 'Hard', desc: 'Senior level' },
];

export default function InterviewSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState(location.state?.resumeId || '');
  const [mode, setMode] = useState('text_chat');
  const [type, setType] = useState('mixed');
  const [difficulty, setDifficulty] = useState('medium');
  const [jobRole, setJobRole] = useState('');
  const [questionCount, setQuestionCount] = useState(10);
  const [loading, setLoading] = useState(false);

  // JD Upload State
  const [jdFile, setJdFile] = useState(null);
  const [jdUploading, setJdUploading] = useState(false);
  const [jdAnalysis, setJdAnalysis] = useState('');
  const [jdSessionId, setJdSessionId] = useState(null);
  const [jdDragging, setJdDragging] = useState(false);

  useEffect(() => {
    resumeAPI.list().then(res => setResumes(res.data.resumes || [])).catch(() => {});
  }, []);

  // JD Upload Handler
  const handleJdUpload = async () => {
    if (!jdFile) return toast.error('Please select a JD file');
    setJdUploading(true);
    try {
      const formData = new FormData();
      formData.append('jd', jdFile);
      const res = await fetch(`${API_BASE}/ai-interview/upload-jd`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setJdSessionId(data.sessionId);
      setJdAnalysis(data.analysis);
      // Try to extract job title from analysis and auto-fill
      const titleMatch = data.analysis.match(/(?:Job Title|Role|Position)[:\s]*([^\n]+)/i);
      if (titleMatch && titleMatch[1] && !jobRole) {
        setJobRole(titleMatch[1].replace(/[\*\#]/g, '').trim());
      }
      toast.success('JD analyzed successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to analyze JD');
    } finally {
      setJdUploading(false);
    }
  };

  const handleJdDrop = (e) => {
    e.preventDefault();
    setJdDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type === 'application/pdf') {
      setJdFile(files[0]);
    } else {
      toast.error('Please drop a PDF file');
    }
  };

  const handleJdSelect = (e) => {
    if (e.target.files.length > 0) {
      setJdFile(e.target.files[0]);
    }
  };

  const removeJd = () => {
    setJdFile(null);
    setJdAnalysis('');
    setJdSessionId(null);
  };

  const handleStart = async () => {
    if (!selectedResume) return toast.error('Please select a CV first');
    if (!jobRole.trim()) return toast.error('Please enter a target job role');
    setLoading(true);
    try {
      const payload = {
        resume_id: selectedResume,
        mode,
        interview_type: type,
        job_role: jobRole,
        difficulty,
        question_count: questionCount,
      };
      // Pass JD context if uploaded
      if (jdSessionId && jdAnalysis) {
        payload.jd_context = jdAnalysis;
      }
      const { data } = await interviewAPI.start(payload);
      toast.success('Interview started!');
      navigate(`/interview/${data.interview.id}`, {
        state: { interview: data.interview, firstQuestion: data.first_question }
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start interview');
    } finally {
      setLoading(false);
    }
  };

  // Simple markdown to HTML
  const formatAnalysis = (text) => {
    if (!text) return '';
    let s = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    s = s.replace(/\*(.+?)\*/g, '<em>$1</em>');
    s = s.replace(/^(\d+)\.\s/gm, '<br/><strong>$1.</strong> ');
    s = s.replace(/^- /gm, '<br/>• ');
    s = s.replace(/\n{2,}/g, '<br/><br/>');
    s = s.replace(/\n/g, '<br/>');
    return s;
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div className="page-title">Setup Your Interview</div>
          <div className="page-subtitle">Configure your mock interview session</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Step 1: Upload JD (NEW) */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 4 }}>1. Upload Job Description <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)' }}>(Optional)</span></div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>Upload a JD and AI will analyze requirements, skills, and auto-fill the job role. Files auto-delete after 30 min.</p>

            {!jdAnalysis ? (
              <>
                <div
                  onDragOver={(e) => { e.preventDefault(); setJdDragging(true); }}
                  onDragLeave={() => setJdDragging(false)}
                  onDrop={handleJdDrop}
                  onClick={() => document.getElementById('jdInput').click()}
                  style={{
                    border: `2px dashed ${jdDragging ? 'var(--primary)' : '#dcdfe6'}`,
                    borderRadius: 12,
                    padding: '32px 20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: jdDragging ? 'var(--primary-light, #eff4ff)' : 'transparent',
                    transition: 'all 0.2s',
                  }}
                >
                  <input type="file" id="jdInput" accept=".pdf" onChange={handleJdSelect} style={{ display: 'none' }} />
                  <div style={{ fontSize: 32, marginBottom: 6 }}>📄</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {jdFile ? jdFile.name : 'Drop JD here or click to browse'}
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>PDF only, max 10MB</div>
                </div>

                {jdFile && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#059669' }}>
                      ✓ {jdFile.name}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13 }}
                        onClick={(e) => { e.stopPropagation(); removeJd(); }}
                      >
                        Remove
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleJdUpload}
                        disabled={jdUploading}
                        style={{ padding: '8px 20px', fontSize: 13 }}
                      >
                        {jdUploading ? (
                          <><span className="spinner" style={{ width: 14, height: 14 }} /> Analyzing...</>
                        ) : (
                          '🔍 Analyze JD'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', background: '#ecfdf5', border: '1px solid #a7f3d0',
                  borderRadius: 10, marginBottom: 12, fontSize: 13, color: '#065f46'
                }}>
                  <span>✓ JD analyzed successfully</span>
                  <button
                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                    onClick={removeJd}
                  >
                    Remove & Re-upload
                  </button>
                </div>
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--primary, #2563eb)', fontWeight: 600, marginBottom: 8 }}>
                    View JD Analysis
                  </summary>
                  <div
                    style={{
                      background: '#f0f1f4', borderRadius: 10, padding: 16,
                      fontSize: 13, lineHeight: 1.7, maxHeight: 250, overflowY: 'auto'
                    }}
                    dangerouslySetInnerHTML={{ __html: formatAnalysis(jdAnalysis) }}
                  />
                </details>
              </>
            )}
          </div>

          {/* Step 2: Select CV */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>2. Select Your CV</div>
            {resumes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>No CVs uploaded yet</p>
                <button className="btn btn-primary" onClick={() => navigate('/upload-cv')}>Upload CV First</button>
              </div>
            ) : (
              <select className="form-input form-select" value={selectedResume} onChange={e => setSelectedResume(e.target.value)}>
                <option value="">Choose a resume...</option>
                {resumes.map(r => (
                  <option key={r.id} value={r.id}>{r.original_filename} — {new Date(r.created_at).toLocaleDateString()}</option>
                ))}
              </select>
            )}
          </div>

          {/* Step 3: Job Role */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>3. Target Job Role</div>
            <input type="text" className="form-input" placeholder="e.g. Senior Process Associate, Team Lead, Quality Analyst..." value={jobRole} onChange={e => setJobRole(e.target.value)} />
            {jdAnalysis && jobRole && (
              <div style={{ fontSize: 12, color: '#059669', marginTop: 6 }}>✓ Auto-filled from JD analysis</div>
            )}
          </div>

          {/* Step 4: Interview Mode */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>4. Choose Interview Mode</div>
            <div className="mode-grid">
              {MODES.map(m => (
                <div key={m.id} className={`card mode-card ${mode === m.id ? 'selected' : ''}`} onClick={() => setMode(m.id)}>
                  <div className="mode-card-icon" style={{ background: mode === m.id ? 'var(--primary-light)' : 'var(--bg)' }}>
                    {m.icon}
                  </div>
                  <div className="mode-card-title">{m.title}</div>
                  <div className="mode-card-desc">{m.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Step 5: Type & Difficulty */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div className="card">
              <div className="card-title" style={{ marginBottom: 16 }}>5. Interview Type</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {TYPES.map(t => (
                  <button key={t.id} className={`btn ${type === t.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setType(t.id)}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: 16 }}>6. Difficulty</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {DIFFICULTIES.map(d => (
                  <button key={d.id} className={`btn ${difficulty === d.id ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }} onClick={() => setDifficulty(d.id)}>
                    <div>{d.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Step 6: Question Count */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>7. Number of Questions</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <input type="range" min="5" max="20" value={questionCount} onChange={e => setQuestionCount(parseInt(e.target.value))} style={{ flex: 1 }} />
              <span style={{ fontSize: 20, fontWeight: 600, minWidth: 40, textAlign: 'center' }}>{questionCount}</span>
            </div>
          </div>

          {/* Start Button */}
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <button className="btn btn-primary btn-lg" onClick={handleStart} disabled={loading} style={{ minWidth: 280 }}>
              {loading ? <><span className="spinner" /> Generating Questions...</> : '🚀 Start Interview'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
